<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, watch } from 'vue';
import { useAuthUserStore } from '@/app/stores/user';
import { connectSocket, disconnectSocket } from '@/app/socket';
import { LISTENER_ACTION_NATIVE } from '@/shared/services/native-app';

import IconFired from '@/shared/assets/icons/fired.vue';
import EntityOnlineObserver from '@/entities/online-observer/index.vue';
import { useI18n } from 'vue-i18n';
import { useQuasarLanguage } from '@/shared/lib/use-quasar-language';
import { useNotificationStore } from '@/app/stores/notification';
import {
  registerInsufficientFundsConfirmationCleanup,
  useInsufficientFundsConfirmation,
} from '@/shared/lib/use-insufficient-funds-confirmation';
import { EntityInsufficientFundsBottomSheet } from '@/entities/insufficient-funds-dialog';

export default defineComponent({
  name: 'App',
  components: { EntityOnlineObserver, IconFired, EntityInsufficientFundsBottomSheet },
  setup() {
    const authUserStore = useAuthUserStore();
    const { locale } = useI18n({ useScope: 'global' });

    const { t } = useI18n();
    const notificationStore = useNotificationStore();
    const isUserAuthorized = computed(() => authUserStore.authorized);
    const {
      isOpen: insufficientFundsOpen,
      paymentAmount: insufficientFundsPaymentAmount,
      availableAmount: insufficientFundsAvailableAmount,
      currency: insufficientFundsCurrency,
      continueInsufficientFunds,
      editInsufficientFunds,
      updateOpenState: updateInsufficientFundsOpenState,
    } = useInsufficientFundsConfirmation();
    registerInsufficientFundsConfirmationCleanup();

    const setWebLanguageLocalStorage = (language?: string) => {
      if (!language && !localStorage.getItem('lang')) localStorage.setItem('lang', 'RU');
      locale.value = language ? language : localStorage.getItem('lang') || 'RU';
      useQuasarLanguage(locale.value);
    };

    watch(
      () => isUserAuthorized.value,
      (value) => {
        if (!value) return disconnectSocket();
        connectSocket();
      },
      { immediate: true }
    );

    const connectionNotification = (e: Event) => {
      notificationStore.setPushNotifications((e as CustomEvent)?.detail || false);
    };

    const setUserContract = (e: Event) => {
      authUserStore.setUserContractFromLocalStorage((e as CustomEvent)?.detail || '');
    };

    const setLanguage = (e: Event) => {
      setWebLanguageLocalStorage((e as CustomEvent)?.detail || '');
    };

    const updateAction = () => {
      authUserStore.getUser();
    };

    //Получаем статус разрешения для пуш уведомлений
    window.addEventListener(
      LISTENER_ACTION_NATIVE.ALLOW_PUSH_NOTIFICATIONS,
      (e: Event) => connectionNotification(e),
      false
    );

    window.addEventListener(LISTENER_ACTION_NATIVE.CHANGE_ORG_REFERENCE_ID, (e: Event) => setUserContract(e), false);
    window.addEventListener(LISTENER_ACTION_NATIVE.CHANGE_WEB_LANGUAGE, (e: Event) => setLanguage(e), false);
    window.addEventListener(LISTENER_ACTION_NATIVE.EMPLOYEE_SECURE_UPDATED, () => updateAction(), false);

    onMounted(setWebLanguageLocalStorage);

    onUnmounted(() => {
      window.removeEventListener(LISTENER_ACTION_NATIVE.CHANGE_WEB_LANGUAGE, (e: Event) => setLanguage(e), false);
      window.removeEventListener(
        LISTENER_ACTION_NATIVE.CHANGE_ORG_REFERENCE_ID,
        (e: Event) => setUserContract(e),
        false
      );
      window.removeEventListener(
        LISTENER_ACTION_NATIVE.ALLOW_PUSH_NOTIFICATIONS,
        (e: Event) => connectionNotification(e),
        false
      );
      window.removeEventListener(LISTENER_ACTION_NATIVE.EMPLOYEE_SECURE_UPDATED, () => updateAction(), false);
    });

    return {
      t,
      authUserStore,
      insufficientFundsOpen,
      insufficientFundsPaymentAmount,
      insufficientFundsAvailableAmount,
      insufficientFundsCurrency,
      continueInsufficientFunds,
      editInsufficientFunds,
      updateInsufficientFundsOpenState,
    };
  },
});
</script>

<template>
  <div class="app">
    <div v-if="authUserStore.isUserNotFound" class="fired-container">
      <div>
        <icon-fired />
        <div class="fired-text">{{ t('AUTH.NOT_FOUND_COMPANY') }}</div>
        <q-btn class="button" label="Выйти" color="primary" no-caps @click="authUserStore.logout" />
      </div>
    </div>
    <RouterView v-else />
    <entity-online-observer />
    <entity-insufficient-funds-bottom-sheet
      :model-value="insufficientFundsOpen"
      :payment-amount="insufficientFundsPaymentAmount"
      :available-amount="insufficientFundsAvailableAmount"
      :currency="insufficientFundsCurrency"
      @update:model-value="updateInsufficientFundsOpenState"
      @edit="editInsufficientFunds"
      @continue="continueInsufficientFunds"
    />
  </div>
</template>

<style lang="scss">
@import './index.scss';

.fired-container {
  display: flex;
  justify-content: center;
  position: relative;
  height: 100vh;
  align-items: center;

  .fired-text {
    font-weight: 400;
    font-size: 14px;
    line-height: 16px;
    color: $tree;
    margin: 24px 0;
    text-align: center;
  }
  .button {
    border-radius: 12px;
    height: 56px;
    width: 100%;
  }
}
</style>
